import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, RefreshCw, Gavel, FileText, Filter 
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SourcingEventCard } from '@/features/sourcing';
import {
  useAvailableSourcingEvents, useSupplierBids 
} from '@/hooks/use-sourcing';
import type { SourcingEventType } from '@/types/sourcing';

type EventFilter = 'all' | SourcingEventType;

function getNoEventsMessage(filter: EventFilter): string {
  if (filter === 'all') {
    return 'There are no open sourcing events in your delivery zones';
  }
  if (filter === 'reverse_auction') {
    return 'No reverse auctions available';
  }
  return 'No sealed proposals available';
}

export function AvailableEventsPage() {
  const navigate = useNavigate();
  const [eventTypeFilter, setEventTypeFilter] = useState<EventFilter>('all');
  const {
    data: eventsData, isLoading, error, refetch 
  } = useAvailableSourcingEvents();
  const { data: bidsData } = useSupplierBids();

  // Map of eventId -> my bid amount
  const myBids = new Map(
    bidsData?.data.map((b) => [b.eventId, {
      total: b.total,
      status: b.status 
    }]) ?? []
  );

  const handleSubmitBid = (eventId: string) => {
    navigate(`/sourcing/submit/${eventId}`);
  };

  const handleViewDetails = (eventId: string) => {
    navigate(`/sourcing/submit/${eventId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="h-72 animate-pulse bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">Failed to load sourcing events</p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allEvents = eventsData?.data ?? [];
  const filteredEvents = eventTypeFilter === 'all' 
    ? allEvents 
    : allEvents.filter((e) => e.eventType === eventTypeFilter);

  // Separate live auctions from others for prominence
  const liveAuctions = filteredEvents.filter((e) => {
    if (e.eventType !== 'reverse_auction') return false;
    const now = Date.now();
    const start = e.auctionStartTime ? new Date(e.auctionStartTime).getTime() : 0;
    const end = e.auctionEndTime ? new Date(e.auctionEndTime).getTime() : 0;
    return now >= start && now < end;
  });

  const otherEvents = filteredEvents.filter((e) => !liveAuctions.includes(e));

  return (
    <div className="space-y-6">
      <PageHeader />

      {/* Filter buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Button
          variant={eventTypeFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setEventTypeFilter('all')}
        >
          All Events
        </Button>
        <Button
          variant={eventTypeFilter === 'reverse_auction' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setEventTypeFilter('reverse_auction')}
          className={eventTypeFilter === 'reverse_auction' ? 'bg-orange-600 hover:bg-orange-700' : ''}
        >
          <Gavel className="h-4 w-4 mr-1" />
          Reverse Auctions
        </Button>
        <Button
          variant={eventTypeFilter === 'sealed_proposal' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setEventTypeFilter('sealed_proposal')}
        >
          <FileText className="h-4 w-4 mr-1" />
          Sealed Proposals
        </Button>
      </div>

      {filteredEvents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No available events</p>
            <p className="text-muted-foreground">
              {getNoEventsMessage(eventTypeFilter)}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Live Auctions Section */}
          {liveAuctions.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2 text-orange-600">
                <Gavel className="h-5 w-5" />
                Live Auctions
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                </span>
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {liveAuctions.map((event) => {
                  const myBid = myBids.get(event.eventId);
                  return (
                    <SourcingEventCard
                      key={event.eventId}
                      event={event}
                      hasBid={!!myBid}
                      myBidAmount={myBid?.total}
                      onSubmitBid={handleSubmitBid}
                      onViewDetails={handleViewDetails}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Other Events */}
          {otherEvents.length > 0 && (
            <div>
              {liveAuctions.length > 0 && (
                <h2 className="text-lg font-semibold mb-3">Other Events</h2>
              )}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {otherEvents.map((event) => {
                  const myBid = myBids.get(event.eventId);
                  return (
                    <SourcingEventCard
                      key={event.eventId}
                      event={event}
                      hasBid={!!myBid}
                      myBidAmount={myBid?.total}
                      onSubmitBid={handleSubmitBid}
                      onViewDetails={handleViewDetails}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-semibold">Available Sourcing Events</h1>
      <p className="text-muted-foreground">
        Browse reverse auctions and sealed proposals in your delivery zones
      </p>
    </div>
  );
}
